import axios from 'axios'
import { BASE_API_URL } from '@/configs'
import { getMetaTrackingParams } from '@/lib/metaPixel'
import {
  IPaymentJobResponse,
  ITransaction,
  IWalletBalance,
  IYaspaDepositResponse,
} from 'types/prize-draw/wallet'
import { AuthBEAPIClient, handleResponseSuccess } from './BEClient'
import { PREFIXES } from './constants'

const WALLET_API_ENDPOINTS = {
  CREATE_PAYMENT_JOB: `/${PREFIXES.GAME}/${PREFIXES.WALLET}/payment-job`,
  CREATE_YASPA_DEPOSIT: `/${PREFIXES.GAME}/${PREFIXES.WALLET}/deposit/yaspa`,
  GET_WALLET_BALANCE: `/${PREFIXES.GAME}/${PREFIXES.WALLET}/balance`,
  GET_WALLET_TRANSACTIONS: `/${PREFIXES.GAME}/${PREFIXES.WALLET}/transactions`,
}

const createPaymentJob = async (amount: number, returnUrlBase: string) => {
  try {
    const response = await AuthBEAPIClient.post<IPaymentJobResponse>(
      WALLET_API_ENDPOINTS.CREATE_PAYMENT_JOB,
      {
        amount,
        currency: 'GBP',
        return_url_success: `${returnUrlBase}?cf_status=success`,
        return_url_failed: `${returnUrlBase}?cf_status=failed`,
        return_url_cancelled: `${returnUrlBase}?cf_status=cancelled`,
        meta: getMetaTrackingParams(),
      },
    )

    return handleResponseSuccess(response)
  } catch (error) {
    throw new Error(`Failed to create payment job: ${error}`)
  }
}

const createYaspaDeposit = async (amount_pence: number, returnUrlBase: string) => {
  try {
    const response = await AuthBEAPIClient.post<IYaspaDepositResponse>(
      WALLET_API_ENDPOINTS.CREATE_YASPA_DEPOSIT,
      {
        amount_pence,
        return_url_success: `${returnUrlBase}?cf_status=success`,
        return_url_failed: `${returnUrlBase}?cf_status=failed`,
        return_url_cancelled: `${returnUrlBase}?cf_status=cancelled`,
        meta: getMetaTrackingParams(),
      },
    )

    return handleResponseSuccess(response)
  } catch (error) {
    throw new Error(`Failed to create Yaspa deposit: ${error}`)
  }
}

const getWalletBalanceSSR = async (accessToken: string) => {
  try {
    const response = await axios.get<IWalletBalance>(
      `${BASE_API_URL}${WALLET_API_ENDPOINTS.GET_WALLET_BALANCE}`,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )

    return handleResponseSuccess(response)
  } catch (error) {
    return null
  }
}

const getWalletBalance = async () => {
  try {
    const response = await AuthBEAPIClient.get<IWalletBalance>(
      WALLET_API_ENDPOINTS.GET_WALLET_BALANCE,
    )

    return handleResponseSuccess(response)
  } catch (error) {
    throw new Error(`Failed to fetch wallet balance: ${error}`)
  }
}

const getWalletTransactionsSSR = async (
  accessToken: string,
  params?: { limit?: number; before_id?: string },
) => {
  try {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.before_id) queryParams.append('before_id', params.before_id)

    const queryString = queryParams.toString()
    const url = `${BASE_API_URL}${WALLET_API_ENDPOINTS.GET_WALLET_TRANSACTIONS}${
      queryString ? `?${queryString}` : ''
    }`

    const response = await axios.get<ITransaction[]>(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })

    return handleResponseSuccess(response)
  } catch (error) {
    return []
  }
}

const getWalletTransactions = async (params?: { limit?: number; before_id?: string }) => {
  try {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.before_id) queryParams.append('before_id', params.before_id)

    const queryString = queryParams.toString()
    const url = `${WALLET_API_ENDPOINTS.GET_WALLET_TRANSACTIONS}${
      queryString ? `?${queryString}` : ''
    }`

    const response = await AuthBEAPIClient.get<ITransaction[]>(url)

    return handleResponseSuccess(response)
  } catch (error) {
    throw new Error(`Failed to fetch wallet transactions: ${error}`)
  }
}

export const walletApi = {
  createPaymentJob,
  createYaspaDeposit,
  getWalletBalanceSSR,
  getWalletBalance,
  getWalletTransactionsSSR,
  getWalletTransactions,
}
